import React, { FC } from 'react'

import { colors, Typography } from '@smarty-nx/ds'
import cn from 'classnames'

import classes from './index.module.scss'
import BubbleHero from '../../components/BubbleHero'
import ColorButton from '../../components/ColorButton'
import PageHead from '../../components/PageHead'
import { SiteContainer } from '../../components/shared'
import spacerClasses from '../../components/styles/spacer.module.scss'
import withApp from '../../components/withApp/withApp'
import android from '../../static/images/android-phone.svg'
import ios from '../../static/images/apple-logo.svg'
import heroImg from '../../static/images/roaming-setup.png'
import windows from '../../static/images/windows-phone.svg'

const RoamingSetup: FC = () => (
  <>
    <BubbleHero>
      <div className={cn(classes.heroWrapper, spacerClasses.spacerBottom)}>
        <img
          src={heroImg}
          alt="Roaming setup."
          className={classes.heroWrapperImage}
          data-testid="roaming-setup-image"
          loading="lazy"
          decoding="async"
        />
        <Typography
          component="h1"
          variant="Kollektif/ExtraLarge"
          className={classes.heroWrapperHeading}
          dataTestId="header"
        >
          Set up your phone <br /> for travelling abroad
        </Typography>
        <Typography
          variant="Kollektif/Small"
          className={classes.heroWrapperParagraph}
          dataTestId="description"
        >
          Most customers can use their SMARTY SIM abroad without changing any
          phone settings. If your phone doesn&apos;t connect while you&apos;re
          away, we&apos;ve created step-by-step guides to help get you up and
          running.
        </Typography>
      </div>
    </BubbleHero>
    <SiteContainer>
      <Typography
        component="h2"
        variant="Kollektif/ExtraLarge"
        className={classes.title}
        dataTestId="which-phone-text"
      >
        Which phone do you have?
      </Typography>
      <div className={cn(classes.row, classes.rowPadded)}>
        <div
          className={cn(classes.card, classes.cardDark)}
          data-testid="ios-card"
        >
          <img
            src={ios}
            alt="IOS"
            className={classes.cardImage}
            data-testid="ios-image"
            loading="lazy"
            decoding="async"
          />
          <Typography
            variant="Kollektif/Small"
            className={classes.cardType}
            dataTestId="ios-text"
          >
            If you have an iPhone, iPad...
          </Typography>
          <ColorButton
            color={colors['Brand/PrimaryGreen']}
            newTab
            href="http://help.smarty.co.uk/set-up-your-phone-to-work-at-home-and-abroad/setting-up-your-ios-device"
            data-testid="ios-button"
          >
            IOS
          </ColorButton>
        </div>
        <div
          className={cn(classes.card, classes.cardDark)}
          data-testid="android-card"
        >
          <img
            src={android}
            alt="Android"
            className={classes.cardImage}
            data-testid="android-image"
            loading="lazy"
            decoding="async"
          />
          <Typography
            variant="Kollektif/Small"
            className={classes.cardType}
            dataTestId="android-text"
          >
            If you have a Samsung, Huawei, Sony...
          </Typography>
          <ColorButton
            color={colors['Brand/PrimaryGreen']}
            newTab
            href="http://help.smarty.co.uk/set-up-your-phone-to-work-at-home-and-abroad/setting-up-your-android-device"
            data-testid="android-button"
          >
            Android
          </ColorButton>
        </div>
        <div
          className={cn(classes.card, classes.cardDark)}
          data-testid="windows-card"
        >
          <img
            src={windows}
            alt="Windows"
            className={classes.cardImage}
            data-testid="windows-image"
            loading="lazy"
            decoding="async"
          />
          <Typography
            variant="Kollektif/Small"
            className={classes.cardType}
            dataTestId="windows-text"
          >
            If you have a Nokia Lumia...
          </Typography>
          <ColorButton
            color={colors['Brand/PrimaryGreen']}
            newTab
            href="http://help.smarty.co.uk/set-up-your-phone-to-work-at-home-and-abroad/setting-up-your-windows-phone"
            data-testid="windows-button"
          >
            Windows Phone
          </ColorButton>
        </div>
      </div>
      <a
        href="http://help.smarty.co.uk/set-up-your-phone-to-work-at-home-and-abroad/setting-up-your-phone-to-work-abroad"
        target="_blank"
        rel="noopener noreferrer"
        className={classes.noneLink}
        data-testid="none-link"
      >
        None of these phones
      </a>
      <div className={cn(classes.row, classes.rowPadded)}>
        <div className={classes.card}>
          <Typography
            component="h3"
            variant="Kollektif/MediumEmphasis"
            className={classes.greenColor}
            dataTestId="why-title"
          >
            Why do I have to do this?
          </Typography>
          <Typography
            variant="Kollektif/Medium"
            className={classes.greyColor}
            dataTestId="why-text"
          >
            Most phones automatically configure the settings needed to use your
            SMARTY SIM abroad when roaming. If your phone doesn&apos;t connect
            as expected, you may need to update your settings using one of the
            guides above.
          </Typography>
        </div>
        <div className={classes.card}>
          <Typography
            component="h3"
            variant="Kollektif/MediumEmphasis"
            className={classes.greenColor}
            dataTestId="when-title"
          >
            When do I have to do it?
          </Typography>
          <Typography
            variant="Kollektif/Medium"
            className={classes.greyColor}
            dataTestId="when-text"
          >
            You don&apos;t need to make any changes unless you&apos;re having
            trouble connecting while abroad. If you do need to update your
            settings, you should only need to do it once.
          </Typography>
        </div>
      </div>
    </SiteContainer>
  </>
)

export default withApp(RoamingSetup)

export const Head = ({ location }: { location: { pathname: string } }) => (
  <PageHead
    title="Roaming Setup | Set Up Your Phone for Roaming"
    description="Travelling abroad? Find out how to set up your SMARTY SIM for roaming before you travel, including APN settings and simple step-by-step guidance."
    location={location}
  />
)
